import { Mail, X } from 'lucide-react';
import { useState, KeyboardEvent, ClipboardEvent, ChangeEvent } from 'react';

interface EmailChipsInputProps {
  emails: string[];
  onChange: (emails: string[]) => void;
  placeholder?: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export const EmailChipsInput = ({
  emails,
  onChange,
  placeholder = 'email@example.com',
  label,
  description,
  disabled = false,
  className = '',
}: EmailChipsInputProps) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Validate email format
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email.trim());
  };

  // Add email to the list
  const addEmail = (email: string) => {
    const trimmedEmail = email.trim();

    if (!trimmedEmail) {
      return;
    }

    if (!isValidEmail(trimmedEmail)) {
      setError('Format d\'email invalide');
      return;
    }

    if (emails.includes(trimmedEmail)) {
      setError('Cet email est déjà ajouté');
      return;
    }

    onChange([...emails, trimmedEmail]);
    setInputValue('');
    setError(null);
  };

  // Remove email from the list
  const removeEmail = (emailToRemove: string) => {
    onChange(emails.filter(email => email !== emailToRemove));
    setError(null);
  };

  // Handle key press (Enter, comma, semicolon, space)
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') {
      e.preventDefault();
      addEmail(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && emails.length > 0) {
      // Remove last email when backspace is pressed on empty input
      removeEmail(emails[emails.length - 1]);
    }
  };

  // Handle paste event to support multiple emails
  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    // Split by common separators: comma, semicolon, space, newline
    const emailsFromPaste = pastedText
      .split(/[,;\s\n]+/)
      .map(email => email.trim())
      .filter(email => email);

    const validEmails: string[] = [];
    const invalidEmails: string[] = [];

    emailsFromPaste.forEach(email => {
      if (isValidEmail(email) && !emails.includes(email)) {
        validEmails.push(email);
      } else if (!isValidEmail(email)) {
        invalidEmails.push(email);
      }
    });

    if (validEmails.length > 0) {
      onChange([...emails, ...validEmails]);
    }

    if (invalidEmails.length > 0) {
      setError(`${invalidEmails.length} email(s) invalide(s) ignoré(s)`);
      setTimeout(() => setError(null), 3000);
    }

    setInputValue('');
  };

  // Handle input change
  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Check if user typed a separator
    if (value.includes(',') || value.includes(';')) {
      const parts = value.split(/[,;]/);
      parts.slice(0, -1).forEach(part => {
        if (part.trim()) {
          addEmail(part);
        }
      });
      setInputValue(parts[parts.length - 1]);
    } else {
      setInputValue(value);
      setError(null);
    }
  };

  // Handle blur to add email if valid
  const handleBlur = () => {
    if (inputValue.trim()) {
      addEmail(inputValue);
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="mb-2 block text-sm font-medium text-neutral-900">
          <Mail className="mr-2 inline h-4 w-4" />
          {label}
        </label>
      )}

      <div
        className={`focus-within:ring-primary-100 focus-within:border-primary-500 min-h-[42px] rounded-lg border border-neutral-300 bg-white px-3 py-2 focus-within:ring-2 ${
          disabled ? 'cursor-not-allowed bg-neutral-100' : ''
        }`}
      >
        <div className="flex flex-wrap gap-2">
          {/* Email chips */}
          {emails.map((email, index) => (
            <div
              key={`${email}-${index}`}
              className="bg-primary-100 text-primary-700 flex items-center gap-1 rounded-md px-2 py-1 text-sm"
            >
              <span>{email}</span>
              {!disabled && (
                <button
                  type="button"
                  onClick={() => removeEmail(email)}
                  className="text-primary-600 hover:text-primary-800 ml-1 flex h-4 w-4 items-center justify-center rounded-full hover:bg-primary-200"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>
          ))}

          {/* Input field */}
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onBlur={handleBlur}
            disabled={disabled}
            placeholder={emails.length === 0 ? placeholder : ''}
            className="min-w-[200px] flex-1 border-none bg-transparent px-1 py-1 text-sm outline-none placeholder:text-neutral-400 disabled:cursor-not-allowed"
          />
        </div>
      </div>

      {/* Description or error */}
      {(description || error) && (
        <p className={`mt-1 text-xs ${error ? 'text-error-600' : 'text-neutral-500'}`}>
          {error || description}
        </p>
      )}

      {/* Helper text */}
      {!error && emails.length === 0 && (
        <p className="mt-1 text-xs text-neutral-500">
          Appuyez sur Entrée, virgule ou point-virgule pour ajouter un email
        </p>
      )}
    </div>
  );
};
